import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";

const inp = "w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]";
const lbl = "block text-xs font-bold uppercase tracking-wide text-gray-500 mb-1";

export default function DocketFormDialog({ open, onOpenChange, entry, fixedClientId, fixedClientName, meta, onSaved }) {
  const { user } = useAuth();
  const [v, setV] = useState({});
  const [team, setTeam] = useState([]);
  const [clients, setClients] = useState([]);
  const [docs, setDocs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [saving, setSaving] = useState(false);

  const blank = useCallback(() => ({
    entry_type: "Task", title: "", client_id: fixedClientId || "",
    event_date: "", due_date: "", court_date: "", sol_date: "", reminder_days: [],
    assigned_attorney_id: "", assigned_staff_id: "", responsible_team: "", description: "",
    linked_document_ids: [], linked_contact_ids: [], status: "open", priority: "normal",
    outcome: "", billable: false, time_spent: "", rate_code: "",
    sync_calendar: false, create_cost: false, cost_amount: "",
  }), [fixedClientId]);

  useEffect(() => {
    if (!open) return;
    api.get("/messages/contacts").then((r) => setTeam(r.data)).catch(() => {});
    api.get("/contacts").then((r) => setContacts(r.data)).catch(() => {});
    if (!fixedClientId) api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    setV(entry ? {
      ...blank(), ...entry,
      assigned_attorney_id: entry.assigned_attorney_id || "",
      assigned_staff_id: entry.assigned_staff_id || "",
      time_spent: entry.time_spent || "", cost_amount: "",
      sync_calendar: Boolean(entry.calendar_event_id), create_cost: false,
    } : blank());
  }, [open, entry, fixedClientId, blank]);

  const cid = fixedClientId || v.client_id;
  useEffect(() => {
    if (!open || !cid) { setDocs([]); return; }
    api.get(`/clients/${cid}/files`).then((r) => setDocs(r.data)).catch(() => setDocs([]));
  }, [open, cid]);

  const set = (k, val) => setV((p) => ({ ...p, [k]: val }));
  const toggleArr = (k, id) => setV((p) => ({ ...p, [k]: p[k].includes(id) ? p[k].filter((x) => x !== id) : [...p[k], id] }));

  const people = [...(user?.id ? [{ id: user.id, name: "Me", email: user.email }] : []),
                  ...team.filter((t) => t.id !== user?.id)];

  const save = async () => {
    if (!v.title.trim()) { toast.error("Add a title / subject"); return; }
    if (!cid) { toast.error("Select a case / client"); return; }
    setSaving(true);
    const body = {
      ...v, client_id: cid,
      assigned_attorney_id: v.assigned_attorney_id || null,
      assigned_staff_id: v.assigned_staff_id || null,
      time_spent: parseFloat(v.time_spent) || 0,
      cost_amount: parseFloat(v.cost_amount) || 0,
    };
    try {
      if (entry) await api.put(`/docket/${entry.id}`, body);
      else await api.post("/docket", body);
      toast.success(entry ? "Docket entry updated" : "Docket entry created");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save entry");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl rounded-sm max-h-[92vh] overflow-y-auto" data-testid="docket-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{entry ? "Edit docket entry" : "New docket entry"}</DialogTitle>
          <DialogDescription className="sr-only">Create or edit a docket entry tied to a case.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className={lbl}>Entry type</label>
            <Select value={v.entry_type} onValueChange={(x) => set("entry_type", x)}>
              <SelectTrigger data-testid="docket-type-trigger" className="rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>{(meta.entry_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className={lbl}>Priority</label>
            <Select value={v.priority} onValueChange={(x) => set("priority", x)}>
              <SelectTrigger data-testid="docket-priority-trigger" className="rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>{(meta.priorities || []).map((p) => <SelectItem key={p} value={p}>{p[0].toUpperCase() + p.slice(1)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <label className={lbl}>Title / subject</label>
        <input data-testid="docket-title-input" className={inp + " mb-3"} value={v.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Motion to Compel Filed" />

        {!fixedClientId ? (
          <div className="mb-3">
            <label className={lbl}>Case / client</label>
            <Select value={v.client_id || ""} onValueChange={(x) => set("client_id", x)}>
              <SelectTrigger data-testid="docket-client-trigger" className="rounded-sm"><SelectValue placeholder="Select case…" /></SelectTrigger>
              <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        ) : (
          <div className="mb-3"><label className={lbl}>Case / client</label>
            <p className="text-sm text-gray-600 px-3 py-2 border border-gray-100 rounded-sm bg-gray-50">{fixedClientName}</p></div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
          <div><label className={lbl}>Event date</label><input type="date" data-testid="docket-event-date" className={inp} value={v.event_date} onChange={(e) => set("event_date", e.target.value)} /></div>
          <div><label className={lbl}>Due date</label><input type="date" data-testid="docket-due-date" className={inp} value={v.due_date} onChange={(e) => set("due_date", e.target.value)} /></div>
          <div><label className={lbl}>Court / hearing</label><input type="date" data-testid="docket-court-date" className={inp} value={v.court_date} onChange={(e) => set("court_date", e.target.value)} /></div>
          <div><label className={lbl + " text-red-600"}>SOL date</label><input type="date" data-testid="docket-sol-date" className={inp} value={v.sol_date} onChange={(e) => set("sol_date", e.target.value)} /></div>
        </div>

        <label className={lbl}>Reminders (days before)</label>
        <div className="flex flex-wrap gap-2 mb-3" data-testid="docket-reminders">
          {(meta.reminder_options || [90, 60, 30, 14, 7, 1]).map((d) => (
            <label key={d} className={`text-xs font-semibold px-2.5 py-1 rounded-full border cursor-pointer ${v.reminder_days?.includes(d) ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-gray-600 border-gray-300"}`}>
              <input type="checkbox" className="hidden" checked={v.reminder_days?.includes(d)} onChange={() => toggleArr("reminder_days", d)} />
              {d}d
            </label>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className={lbl}>Assigned attorney</label>
            <Select value={v.assigned_attorney_id || "none"} onValueChange={(x) => set("assigned_attorney_id", x === "none" ? "" : x)}>
              <SelectTrigger data-testid="docket-attorney-trigger" className="rounded-sm"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent><SelectItem value="none">None</SelectItem>{people.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className={lbl}>Assigned staff</label>
            <Select value={v.assigned_staff_id || "none"} onValueChange={(x) => set("assigned_staff_id", x === "none" ? "" : x)}>
              <SelectTrigger data-testid="docket-staff-trigger" className="rounded-sm"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent><SelectItem value="none">None</SelectItem>{people.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <label className={lbl}>Responsible team</label>
        <input className={inp + " mb-3"} value={v.responsible_team} onChange={(e) => set("responsible_team", e.target.value)} placeholder="e.g. Litigation team" />

        <label className={lbl}>Description / notes</label>
        <textarea data-testid="docket-description" className={inp + " mb-3"} rows={3} value={v.description} onChange={(e) => set("description", e.target.value)} placeholder="Detailed notes, strategy, internal commentary…" />

        {cid && (docs.length > 0 || contacts.length > 0) && (
          <div className="grid grid-cols-2 gap-3 mb-3">
            {docs.length > 0 && (
              <div>
                <label className={lbl}>Linked documents</label>
                <div className="border border-gray-200 rounded-sm max-h-28 overflow-y-auto p-1" data-testid="docket-docs">
                  {docs.map((f) => (
                    <label key={f.id} className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 rounded-sm cursor-pointer text-xs">
                      <input type="checkbox" checked={v.linked_document_ids?.includes(f.id)} onChange={() => toggleArr("linked_document_ids", f.id)} className="accent-[#002FA7]" />
                      <span className="truncate text-gray-700">{f.original_filename}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            {contacts.length > 0 && (
              <div>
                <label className={lbl}>Linked contacts</label>
                <div className="border border-gray-200 rounded-sm max-h-28 overflow-y-auto p-1" data-testid="docket-contacts">
                  {contacts.map((c) => (
                    <label key={c.id} className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 rounded-sm cursor-pointer text-xs">
                      <input type="checkbox" checked={v.linked_contact_ids?.includes(c.id)} onChange={() => toggleArr("linked_contact_ids", c.id)} className="accent-[#002FA7]" />
                      <span className="truncate text-gray-700">{c.name} · {c.type}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className={lbl}>Status</label>
            <Select value={v.status} onValueChange={(x) => set("status", x)}>
              <SelectTrigger data-testid="docket-status-trigger" className="rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>{(meta.statuses || []).map((s) => <SelectItem key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className={lbl}>Outcome / result</label>
            <Select value={v.outcome || "none"} onValueChange={(x) => set("outcome", x === "none" ? "" : x)}>
              <SelectTrigger data-testid="docket-outcome-trigger" className="rounded-sm"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent><SelectItem value="none">—</SelectItem>{(meta.outcomes || []).filter(Boolean).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <div className="border border-gray-100 rounded-sm p-3 bg-gray-50/60 mb-3">
          <label className="flex items-center gap-2 text-sm text-gray-700 mb-2" data-testid="docket-billable-toggle">
            <input type="checkbox" checked={v.billable} onChange={(e) => set("billable", e.target.checked)} className="accent-[#002FA7] h-4 w-4" /> Billable
          </label>
          {v.billable && (
            <div className="grid grid-cols-3 gap-3">
              <div><label className={lbl}>Time spent (hrs)</label><input type="number" min="0" step="0.1" className={inp} value={v.time_spent} onChange={(e) => set("time_spent", e.target.value)} /></div>
              <div><label className={lbl}>Rate / fee code</label><input className={inp} value={v.rate_code} onChange={(e) => set("rate_code", e.target.value)} /></div>
              <div><label className={lbl}>Cost amount ($)</label><input type="number" min="0" step="0.01" data-testid="docket-cost-amount" className={inp} value={v.cost_amount} onChange={(e) => set("cost_amount", e.target.value)} /></div>
              {!entry && (
                <label className="col-span-3 flex items-center gap-2 text-xs text-gray-600" data-testid="docket-create-cost-toggle">
                  <input type="checkbox" checked={v.create_cost} onChange={(e) => set("create_cost", e.target.checked)} className="accent-[#002FA7]" /> Also add this as a Cost on the case
                </label>
              )}
            </div>
          )}
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-700 mb-1" data-testid="docket-sync-calendar-toggle">
          <input type="checkbox" checked={v.sync_calendar} onChange={(e) => set("sync_calendar", e.target.checked)} className="accent-[#002FA7] h-4 w-4" /> Add to my Microsoft 365 calendar
        </label>

        <DialogFooter className="mt-4">
          <button onClick={() => onOpenChange(false)} className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-sm font-semibold text-sm">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="docket-save-button"
            className="bg-[#002FA7] hover:bg-[#002480] text-white px-4 py-2 rounded-sm font-semibold text-sm disabled:opacity-60">
            {saving ? "Saving…" : entry ? "Save changes" : "Create entry"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
